import { addWeeks } from 'date-fns'
import { useQuery } from 'react-query'
import { getAvailabilities, GetAvailabilitiesParams } from 'src/services/api/availabilities'
import { getPractitionerReferenceIds } from 'src/utils/getPractitionerReferenceId'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { usePractitionersContext } from '@context/PractitionersContext'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'

const STALE_TIME_MINUTES = 5 * 60 * 1000
const CACHE_TIME_MINUTES = 2 * 60 * 1000

export const useNearestAvailabilities = () => {
  const { clinicId } = useConfig()
  const { treatmentBundleTypeReferenceId, insuranceType, patientType, preferredPractitionerReferenceIds } =
    useChoiceState()
  const { allPractitioners } = usePractitionersContext()
  const allPractitionersSelected = allPractitioners?.length === preferredPractitionerReferenceIds?.length

  const startOfDay = new Date()
  const sixWeeksLater = addWeeks(startOfDay, 6)

  const params: GetAvailabilitiesParams = {
    start: startOfDay.toISOString(),
    end: sixWeeksLater.toISOString(),
    treatmentBundleReferenceId: treatmentBundleTypeReferenceId,
    clinicReferenceId: clinicId,
    insuranceType,
    employeeReferenceIds: allPractitionersSelected
      ? []
      : getPractitionerReferenceIds(preferredPractitionerReferenceIds),
    patientType: patientType || PatientTypeOptions.NEW,
    maxDaysWithSlots: 1,
    maxSlots: 5,
  }

  const hasRequiredParams = !!treatmentBundleTypeReferenceId && !!clinicId

  return useQuery(
    [
      'nearestAvailabilities',
      treatmentBundleTypeReferenceId,
      clinicId,
      insuranceType,
      patientType || PatientTypeOptions.NEW,
    ],
    () => getAvailabilities(params),
    {
      staleTime: STALE_TIME_MINUTES,
      cacheTime: CACHE_TIME_MINUTES,
      enabled: hasRequiredParams,
      keepPreviousData: true,
    }
  )
}
